/**
 * Pure selection helpers for the Working Orders panel (EF-187a).
 *
 * Operates ONLY on the adapter-normalized order shape from `orderAdapter.js`:
 *   { side, price, stopPrice, quantity, accountNumber, orderId, cancelable, status, ... }
 *
 * No React, no Schwab raw shape, no `childOrderStrategies`. If the adapter
 * shape changes, this file updates and nothing else does.
 */

export function classifyOrder(order) {
  const side = order?.side || ''
  if (side === 'BUY' || side === 'BUY_TO_OPEN') return 'buy'
  const hasStop = order?.stopPrice != null && order.stopPrice !== ''
  const hasPrice = order?.price != null && order.price !== ''
  if (side.includes('SELL') && hasStop && !hasPrice) return 'stop'
  if (side.includes('SELL') && hasPrice && !hasStop) return 'tp'
  return 'other'
}

export function firstTapMatchSet(seedOrder, candidateOrders, { activeStatuses }) {
  // Stateless: re-classify the seed on every call. No memory of prior selections.
  const seedType = classifyOrder(seedOrder)
  const set = new Set()
  if (seedOrder?.orderId != null) set.add(seedOrder.orderId)

  const seedMatchPrice = seedType === 'buy'
    ? (seedOrder.stopPrice ?? seedOrder.price)
    : seedType === 'stop'
      ? seedOrder.stopPrice
      : seedType === 'tp'
        ? seedOrder.price
        : null

  if (seedMatchPrice == null) return set

  for (const o of candidateOrders) {
    if (!o || o.orderId === seedOrder.orderId) continue
    if (!o.cancelable) continue
    if (!activeStatuses.has(o.status)) continue
    const oType = classifyOrder(o)
    if (oType !== seedType) continue
    const oMatchPrice = oType === 'buy'
      ? (o.stopPrice ?? o.price)
      : oType === 'stop'
        ? o.stopPrice
        : o.price
    if (oMatchPrice === seedMatchPrice) set.add(o.orderId)
  }

  return set
}

export function buildBatchMoveOrders({ selectedOrders, newPrice, symbol }) {
  if (!selectedOrders || selectedOrders.length === 0) return []
  const types = new Set(selectedOrders.map(classifyOrder))
  if (types.size > 1) {
    throw new Error('buildBatchMoveOrders: cannot batch a mixed selection')
  }
  const [type] = [...types]
  const sym = symbol.toUpperCase()

  if (type === 'stop') {
    return selectedOrders.map(o => ({
      account_number: o.accountNumber,
      symbol: sym,
      side: 'SELL',
      quantity: o.quantity || o.remainingQuantity || 0,
      orderType: 'STOP',
      stopPrice: newPrice,
      duration: 'GOOD_TILL_CANCEL',
      session: 'NORMAL',
      assetType: 'EQUITY',
      existingOrderId: o.orderId,
    }))
  }
  if (type === 'tp') {
    return selectedOrders.map(o => ({
      account_number: o.accountNumber,
      symbol: sym,
      side: 'SELL',
      quantity: o.quantity || o.remainingQuantity || 0,
      orderType: 'LIMIT',
      price: newPrice,
      duration: 'GOOD_TILL_CANCEL',
      session: 'NORMAL',
      assetType: 'EQUITY',
      existingOrderId: o.orderId,
    }))
  }
  throw new Error('buildBatchMoveOrders: cannot batch a mixed selection')
}
